import { getDisplayToken } from '@/domain/hooks/useDisplayToken'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { SuccessViewCheckmark } from '@/features/dialogs/common/components/success-view/SuccessViewCheckmark'
import { SuccessViewContent } from '@/features/dialogs/common/components/success-view/SuccessViewContent'
import { SuccessViewProceedButton } from '@/features/dialogs/common/components/success-view/SuccessViewProceedButton'
import { SuccessViewSummaryPanel } from '@/features/dialogs/common/components/success-view/SuccessViewSummaryPanel'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { useChainId } from 'wagmi'

export interface BorrowSuccessViewProps {
  collateralToken?: Token
  collateralAmount?: NormalizedUnitNumber
  borrowToken: Token
  borrowAmount: NormalizedUnitNumber
  onProceed: () => void
  proceedText?: string
}

export function BorrowSuccessView({
  collateralToken,
  collateralAmount,
  borrowToken,
  borrowAmount,
  onProceed,
  proceedText = 'Done',
}: BorrowSuccessViewProps) {
  const chainId = useChainId()
  const displayBorrowToken = getDisplayToken(chainId, borrowToken)
  const displayCollateralToken = collateralToken ? getDisplayToken(chainId, collateralToken) : undefined

  const hasCollateral = collateralToken && collateralAmount && Number(collateralAmount) > 0

  return (
    <SuccessViewContent>
      <SuccessViewCheckmark isModal />

      <div className="flex w-full flex-col gap-3">
        {/* Collateral Panel (if provided) */}
        {hasCollateral && collateralToken && collateralAmount && displayCollateralToken && (
          <SuccessViewSummaryPanel title="Deposited">
            <div className="flex items-center gap-2">
              <TokenIcon token={collateralToken} className="h-6" />
              <span className="font-semibold">{displayCollateralToken.symbol}</span>
            </div>
            <div className="flex grow flex-col">
              <Typography className="text-right font-primary">
                {collateralToken.format(collateralAmount, { style: 'auto' })}
              </Typography>
            </div>
          </SuccessViewSummaryPanel>
        )}

        {/* Borrow Panel */}
        <SuccessViewSummaryPanel title="Borrowed">
          <div className="flex items-center gap-2">
            <TokenIcon token={borrowToken} className="h-6" />
            <span className="font-semibold">{displayBorrowToken.symbol}</span>
          </div>
          <div className="flex grow flex-col">
            <Typography className="text-right font-primary">
              {borrowToken.format(borrowAmount, { style: 'auto' })}
            </Typography>
          </div>
        </SuccessViewSummaryPanel>
      </div>

      <SuccessViewProceedButton onProceed={onProceed}>{proceedText}</SuccessViewProceedButton>
    </SuccessViewContent>
  )
}

